import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { WarningBadge } from "@/components/Badges";
import { ListX, Search as SearchIcon, ChevronRight, AlertTriangle } from "lucide-react";

export default function Blacklist() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/blacklist").then((r) => setRows(r.data)).finally(() => setLoading(false));
  }, []);

  const filtered = rows.filter((r) => {
    const t = q.toLowerCase();
    return (
      r.customer_name?.toLowerCase().includes(t) ||
      r.cccd_number?.toLowerCase().includes(t) ||
      r.phone?.toLowerCase().includes(t) ||
      r.reported_by_name?.toLowerCase().includes(t)
    );
  });

  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-2">Dữ liệu</div>
      <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter text-white flex items-center gap-3">
        <ListX className="w-9 h-9 text-primary" /> Danh sách đen
      </h1>
      <p className="text-zinc-500 mt-2 mb-6">Các báo cáo đã được duyệt. Số CCCD/SĐT được che một phần theo quy định bảo vệ dữ liệu.</p>

      <div className="relative mb-5 max-w-md">
        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
        <input
          data-testid="blacklist-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Lọc theo tên, CCCD, SĐT, nhà xe..."
          className="w-full bg-[#0a0a0a] border border-zinc-800 text-white rounded-sm pl-10 pr-4 py-2.5 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors placeholder:text-zinc-600"
        />
      </div>

      {loading ? (
        <p className="text-zinc-600 py-10 text-center">Đang tải...</p>
      ) : filtered.length === 0 ? (
        <p className="text-zinc-600 py-10 text-center">Không có dữ liệu.</p>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block bg-card border border-zinc-800 rounded-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-800 text-left text-xs uppercase tracking-wider text-zinc-500">
                  <th className="px-4 py-3 font-semibold">Tên khách</th>
                  <th className="px-4 py-3 font-semibold">SĐT</th>
                  <th className="px-4 py-3 font-semibold">Số CCCD</th>
                  <th className="px-4 py-3 font-semibold">Mức độ</th>
                  <th className="px-4 py-3 font-semibold">Nhà xe báo cáo</th>
                  <th className="px-4 py-3 font-semibold">Ngày</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr
                    key={r.id}
                    data-testid={`blacklist-row-${r.id}`}
                    className="border-b border-zinc-800/60 hover:bg-zinc-900/50 transition-colors"
                  >
                    <td className="px-4 py-3 font-semibold text-white">
                      <div className="flex items-center gap-2">
                        {r.customer_name}
                        {r.report_count > 1 && (
                          <span data-testid={`repeat-badge-${r.id}`} className="inline-flex items-center gap-1 text-[10px] font-black uppercase bg-red-500/20 text-red-400 border border-red-500 rounded-sm px-1.5 py-0.5">
                            <AlertTriangle className="w-3 h-3" /> {r.report_count}x
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-zinc-300">{r.phone || "—"}</td>
                    <td className="px-4 py-3 text-zinc-300 font-mono">{r.cccd_number || "—"}</td>
                    <td className="px-4 py-3"><WarningBadge level={r.warning_level} /></td>
                    <td className="px-4 py-3 text-zinc-400">{r.reported_by_name}</td>
                    <td className="px-4 py-3 text-zinc-500">{new Date(r.report_date).toLocaleDateString("vi-VN")}</td>
                    <td className="px-4 py-3 text-right">
                      <Link to={`/danh-sach-den/${r.id}`} className="text-primary hover:text-[#ff1a1a] inline-flex items-center gap-1 text-sm font-semibold">
                        Chi tiết <ChevronRight className="w-4 h-4" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {filtered.map((r) => (
              <Link
                key={r.id}
                to={`/danh-sach-den/${r.id}`}
                data-testid={`blacklist-card-${r.id}`}
                className="block bg-card border border-zinc-800 rounded-sm p-4 hover:border-primary/40 transition-colors"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="font-semibold text-white text-base flex items-center gap-2">
                    {r.customer_name}
                    {r.report_count > 1 && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase bg-red-500/20 text-red-400 border border-red-500 rounded-sm px-1.5 py-0.5">
                        <AlertTriangle className="w-3 h-3" /> {r.report_count}x
                      </span>
                    )}
                  </div>
                  <WarningBadge level={r.warning_level} />
                </div>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between"><span className="text-zinc-500">SĐT</span><span className="text-zinc-300">{r.phone || "—"}</span></div>
                  <div className="flex justify-between"><span className="text-zinc-500">CCCD</span><span className="text-zinc-300 font-mono">{r.cccd_number || "—"}</span></div>
                  <div className="flex justify-between"><span className="text-zinc-500">Nhà xe</span><span className="text-zinc-300">{r.reported_by_name}</span></div>
                  <div className="flex justify-between"><span className="text-zinc-500">Ngày</span><span className="text-zinc-300">{new Date(r.report_date).toLocaleDateString("vi-VN")}</span></div>
                </div>
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
